// src/v2/leases/RenewalDecisionModal.jsx
//
// P-15: LEASE-RENEWAL-DECISION-REGISTER
//
// Shared decision-panel modal. Two render paths:
//
//   Deferred path (days_to_next_action > 1825 OR no action date):
//     Shows window-open date, a reminder-note field, and a "Log deferred" CTA.
//     Writes a decision row with status='not_yet'.
//
//   Full decision form:
//     4-option disposition radio (Renew / Renegotiate / Sublease-exit / Let-auto-roll),
//     optional recommendation chip (derived from bucket), owner name + role, target date,
//     rationale textarea, expected action textarea.
//     "Log decision" CTA writes with status='logged'.
//
// Props:
//   lease         — object with { leaseId, locationId, locationAlias, locationMarket, endDate,
//                                  days_to_next_action?, action_window_bucket? }
//   criticalDates — Array (may be empty; modal degrades gracefully)
//   onClose       — () → void
//   onLogged      — () → void  called after a successful write

import React, { useState } from "react";
import { T } from "../../app/tokens.js";
import { logRenewalDecision } from "../../data/renewalDecisionsClient.js";
import { showToast } from "../../components/toastService.jsx";

// ─── Disposition palette ──────────────────────────────────────────────────────

const DISPOSITION_OPTIONS = [
  {
    value:       "renew",
    label:       "Renew",
    description: "Exercise renewal option at current location",
    bg:          T.ragGreenBg,
    border:      T.ragGreenBorder,
    fg:          T.ragGreenText,
    dot:         T.green,
  },
  {
    value:       "renegotiate",
    label:       "Renegotiate",
    description: "Renegotiate terms — rent, size, or tenure",
    bg:          T.ragAmberBg,
    border:      T.ragAmberBorder,
    fg:          T.ragAmberText,
    dot:         T.ragAmber,
  },
  {
    value:       "sublease_exit",
    label:       "Sublease exit",
    description: "Sublease the space and exit the lease",
    bg:          T.chipExpiryBg,
    border:      T.ragRedBorder,
    fg:          T.ragRed,
    dot:         T.urgentRed,
  },
  {
    value:       "let_auto_roll",
    label:       "Let auto-roll",
    description: "Let the lease roll on its current terms",
    bg:          T.lightGray,
    border:      T.border,
    fg:          T.mid,
    dot:         T.mid,
  },
];

// ─── Recommendation chip ──────────────────────────────────────────────────────

const BUCKET_RECOMMENDATION = {
  "decide-now": { label: "Recommended: Renew or Renegotiate", color: T.urgentRed },
  "negotiate":  { label: "Recommended: Renegotiate",          color: T.ragAmber  },
};

function RecommendationChip({ bucket }) {
  const rec = BUCKET_RECOMMENDATION[bucket];
  if (!rec) return null;
  return (
    <div
      data-testid="decision-recommendation-chip"
      style={{
        display:      "inline-flex",
        alignItems:   "center",
        gap:          5,
        fontSize:     T.fsXs,
        fontWeight:   T.fwSemibold,
        color:        rec.color,
        background:   T.lightGray,
        border:       `1px solid ${T.border}`,
        borderRadius: 6,
        padding:      "3px 10px",
        marginBottom: 12,
      }}
    >
      <span style={{ width: 7, height: 7, borderRadius: "50%", background: rec.color, flexShrink: 0, display: "inline-block" }} />
      {rec.label}
    </div>
  );
}

// ─── Status chip (for past decisions) ─────────────────────────────────────────

const STATUS_STYLE = {
  logged:    { bg: T.blueSoft,    fg: T.blue,         label: "Logged"    },
  in_motion: { bg: T.ragAmberBg,  fg: T.ragAmberText, label: "In motion" },
  verified:  { bg: T.ragGreenBg,  fg: T.ragGreenText, label: "Verified"  },
  not_yet:   { bg: T.lightGray,   fg: T.mid,          label: "Not yet"   },
};

export function DecisionStatusChip({ status }) {
  const s = STATUS_STYLE[status] ?? STATUS_STYLE.logged;
  return (
    <span
      data-testid="decision-status-chip"
      style={{
        display:      "inline-flex",
        alignItems:   "center",
        justifyContent: "center",
        fontSize:     T.fsXxs,
        fontWeight:   T.fwSemibold,
        background:   s.bg,
        color:        s.fg,
        borderRadius: 5,
        padding:      "1px 8px",
        whiteSpace:   "nowrap",
      }}
    >
      {s.label}
    </span>
  );
}

// Disposition badge for use in register / past-decisions list
const DISP_STYLE = {
  renew:         { bg: T.ragGreenBg,   fg: T.ragGreenText, label: "Renew"          },
  renegotiate:   { bg: T.ragAmberBg,   fg: T.ragAmberText, label: "Renegotiate"    },
  sublease_exit: { bg: T.chipExpiryBg, fg: T.ragRed,       label: "Sublease exit"  },
  let_auto_roll: { bg: T.lightGray,    fg: T.mid,          label: "Let auto-roll"  },
};

export function DispositionBadge({ disposition }) {
  const s = DISP_STYLE[disposition] ?? DISP_STYLE.let_auto_roll;
  return (
    <span
      data-testid="decision-disposition-badge"
      style={{
        display:      "inline-flex",
        alignItems:   "center",
        justifyContent: "center",
        fontSize:     T.fsXxs,
        fontWeight:   T.fwSemibold,
        background:   s.bg,
        color:        s.fg,
        borderRadius: 5,
        padding:      "1px 8px",
        whiteSpace:   "nowrap",
      }}
    >
      {s.label}
    </span>
  );
}

// ─── Modal overlay ────────────────────────────────────────────────────────────

export function RenewalDecisionModal({ lease, criticalDates = [], onClose, onLogged }) {
  const daysToAction = lease?.days_to_next_action ?? null;
  const bucket       = lease?.action_window_bucket ?? null;

  // Deferred path: no action date yet, or action > 5 years away
  const isDeferred = daysToAction === null || daysToAction > 1825;

  // Form state
  const [disposition,     setDisposition]     = useState("renew");
  const [ownerName,       setOwnerName]        = useState("");
  const [ownerRole,       setOwnerRole]        = useState("");
  const [targetDate,      setTargetDate]       = useState("");
  const [rationale,       setRationale]        = useState("");
  const [expectedAction,  setExpectedAction]   = useState("");
  const [deferralNote,    setDeferralNote]     = useState("");
  const [saving,          setSaving]           = useState(false);
  const [error,           setError]            = useState(null);

  // When the window opens (deferred path), compute a human-readable date
  // based on when action becomes relevant (days_to_next_action - 1825)
  // But since it's deferred we just show a generic message about the lease end.
  const leaseAlias  = lease?.locationAlias  || lease?.locationMarket || "this location";
  const leaseMarket = lease?.locationMarket || "";

  async function handleSubmit() {
    if (saving) return;
    setSaving(true);
    setError(null);
    try {
      const locId   = lease?.locationId;
      const leaseId = lease?.leaseId || lease?.id;
      if (!locId || !leaseId) {
        throw new Error("Missing location or lease identifier.");
      }

      const payload = isDeferred
        ? {
            disposition:    "let_auto_roll",
            status:         "not_yet",
            rationale:      deferralNote || null,
          }
        : {
            disposition,
            ownerName:      ownerName  || null,
            ownerRole:      ownerRole  || null,
            targetDate:     targetDate || null,
            rationale:      rationale  || null,
            expectedAction: expectedAction || null,
            status:         "logged",
          };

      await logRenewalDecision(locId, leaseId, payload);
      showToast("Decision logged.", {
        type: "success",
        action: { label: "View decisions", onClick: () => { window.location.href = "/radar/decisions"; } },
      });
      if (onLogged) onLogged();
      if (onClose) onClose();
    } catch (err) {
      setError(err.message || "Failed to log decision — please try again.");
    } finally {
      setSaving(false);
    }
  }

  return (
    // Scrim
    <div
      data-testid="renewal-decision-modal-scrim"
      onClick={(e) => { if (e.target === e.currentTarget && onClose) onClose(); }}
      style={{
        position:       "fixed",
        inset:          0,
        background:     "rgba(10,24,46,0.45)",
        zIndex:         1000,
        display:        "flex",
        alignItems:     "center",
        justifyContent: "center",
        padding:        "24px 16px",
      }}
    >
      {/* Panel */}
      <div
        data-testid="renewal-decision-modal"
        style={{
          background:   T.card,
          borderRadius: 14,
          boxShadow:    "0 20px 60px rgba(10,24,46,0.25)",
          width:        "100%",
          maxWidth:     520,
          maxHeight:    "90vh",
          display:      "flex",
          flexDirection:"column",
          overflow:     "hidden",
        }}
      >
        {/* Header */}
        <div style={{
          padding:      "18px 20px 14px",
          borderBottom: `1px solid ${T.border}`,
          display:      "flex",
          alignItems:   "flex-start",
          justifyContent: "space-between",
          flexShrink:   0,
        }}>
          <div>
            <div style={{ fontSize: T.fsMd, fontWeight: T.fwBold, color: T.dark }}>
              {isDeferred ? "Log deferred decision" : "Start a renewal decision"}
            </div>
            <div style={{ fontSize: T.fsXs, color: T.mid, marginTop: 3 }}>
              {leaseMarket ? `${leaseMarket} · ` : ""}{leaseAlias}
            </div>
          </div>
          <button
            type="button"
            data-testid="renewal-decision-modal-close"
            onClick={onClose}
            aria-label="Close"
            style={{
              background:   "none",
              border:       "none",
              color:        T.mid,
              fontSize:     T.fsLg,
              cursor:       "pointer",
              padding:      "0 4px",
              lineHeight:   1,
              marginTop:    -2,
            }}
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: "auto", padding: "18px 20px" }}>

          {isDeferred ? (
            /* ── Deferred path ─────────────────────────────────────────── */
            <div>
              <div style={{
                background:   T.blueSoft,
                border:       `1px solid ${T.blueSoftBorder}`,
                borderRadius: 9,
                padding:      "12px 14px",
                marginBottom: 16,
                fontSize:     T.fsSm,
                color:        T.dark,
                lineHeight:   1.55,
              }}>
                <strong>No decision needed yet.</strong> The next action window for this lease
                opens more than 5 years from today. Log a deferred note to record
                your current thinking.
              </div>

              <label style={{ display: "block", fontSize: T.fsXs, fontWeight: T.fwSemibold, color: T.dark, marginBottom: 4 }}>
                Reminder note <span style={{ fontWeight: T.fwRegular, color: T.mid }}>(optional)</span>
              </label>
              <textarea
                data-testid="decision-deferral-note"
                value={deferralNote}
                onChange={(e) => setDeferralNote(e.target.value)}
                placeholder="Any notes about this lease to revisit when the window opens…"
                rows={4}
                style={{
                  width:        "100%",
                  fontSize:     T.fsSm,
                  padding:      "8px 10px",
                  borderRadius: 8,
                  border:       `1px solid ${T.border}`,
                  resize:       "vertical",
                  fontFamily:   "inherit",
                  boxSizing:    "border-box",
                }}
              />
            </div>
          ) : (
            /* ── Full decision form ────────────────────────────────────── */
            <div>
              {/* Recommendation chip */}
              <RecommendationChip bucket={bucket} />

              {/* Disposition radios */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: T.fsXs, fontWeight: T.fwSemibold, color: T.dark, marginBottom: 8 }}>
                  What is the intended direction?
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
                  {DISPOSITION_OPTIONS.map((opt) => {
                    const selected = disposition === opt.value;
                    return (
                      <label
                        key={opt.value}
                        data-testid={`decision-disposition-${opt.value}`}
                        style={{
                          display:      "flex",
                          alignItems:   "center",
                          gap:          10,
                          padding:      "9px 12px",
                          borderRadius: 9,
                          border:       `1.5px solid ${selected ? opt.border : T.border}`,
                          background:   selected ? opt.bg : T.card,
                          cursor:       "pointer",
                          transition:   "all .12s",
                        }}
                      >
                        <input
                          type="radio"
                          name="disposition"
                          value={opt.value}
                          checked={selected}
                          onChange={() => setDisposition(opt.value)}
                          style={{ accentColor: opt.dot, flexShrink: 0 }}
                        />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: T.fsSm, fontWeight: T.fwSemibold, color: selected ? opt.fg : T.dark }}>
                            {opt.label}
                          </div>
                          <div style={{ fontSize: T.fsXxs, color: T.mid, marginTop: 1 }}>
                            {opt.description}
                          </div>
                        </div>
                        <span style={{
                          width:        8,
                          height:       8,
                          borderRadius: "50%",
                          background:   selected ? opt.dot : T.lightGray,
                          flexShrink:   0,
                          transition:   "background .12s",
                        }} />
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* Owner */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 12 }}>
                <div>
                  <label style={{ display: "block", fontSize: T.fsXs, fontWeight: T.fwSemibold, color: T.dark, marginBottom: 4 }}>
                    Owner name
                  </label>
                  <input
                    type="text"
                    data-testid="decision-owner-name"
                    value={ownerName}
                    onChange={(e) => setOwnerName(e.target.value)}
                    placeholder="e.g. Alex Chen"
                    style={{
                      width:        "100%",
                      fontSize:     T.fsSm,
                      padding:      "7px 9px",
                      borderRadius: 7,
                      border:       `1px solid ${T.border}`,
                      boxSizing:    "border-box",
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: "block", fontSize: T.fsXs, fontWeight: T.fwSemibold, color: T.dark, marginBottom: 4 }}>
                    Role
                  </label>
                  <input
                    type="text"
                    data-testid="decision-owner-role"
                    value={ownerRole}
                    onChange={(e) => setOwnerRole(e.target.value)}
                    placeholder="e.g. Head of Real Estate"
                    style={{
                      width:        "100%",
                      fontSize:     T.fsSm,
                      padding:      "7px 9px",
                      borderRadius: 7,
                      border:       `1px solid ${T.border}`,
                      boxSizing:    "border-box",
                    }}
                  />
                </div>
              </div>

              {/* Target date */}
              <div style={{ marginBottom: 12 }}>
                <label style={{ display: "block", fontSize: T.fsXs, fontWeight: T.fwSemibold, color: T.dark, marginBottom: 4 }}>
                  Target decision date <span style={{ fontWeight: T.fwRegular, color: T.mid }}>(optional)</span>
                </label>
                <input
                  type="date"
                  data-testid="decision-target-date"
                  value={targetDate}
                  onChange={(e) => setTargetDate(e.target.value)}
                  style={{
                    fontSize:     T.fsSm,
                    padding:      "7px 9px",
                    borderRadius: 7,
                    border:       `1px solid ${T.border}`,
                    width:        "100%",
                    boxSizing:    "border-box",
                  }}
                />
              </div>

              {/* Rationale */}
              <div style={{ marginBottom: 12 }}>
                <label style={{ display: "block", fontSize: T.fsXs, fontWeight: T.fwSemibold, color: T.dark, marginBottom: 4 }}>
                  Rationale <span style={{ fontWeight: T.fwRegular, color: T.mid }}>(optional)</span>
                </label>
                <textarea
                  data-testid="decision-rationale"
                  value={rationale}
                  onChange={(e) => setRationale(e.target.value)}
                  placeholder="Why this direction? Key factors driving the decision…"
                  rows={3}
                  style={{
                    width:        "100%",
                    fontSize:     T.fsSm,
                    padding:      "7px 9px",
                    borderRadius: 7,
                    border:       `1px solid ${T.border}`,
                    resize:       "vertical",
                    fontFamily:   "inherit",
                    boxSizing:    "border-box",
                  }}
                />
              </div>

              {/* Expected action */}
              <div style={{ marginBottom: 4 }}>
                <label style={{ display: "block", fontSize: T.fsXs, fontWeight: T.fwSemibold, color: T.dark, marginBottom: 4 }}>
                  Expected action <span style={{ fontWeight: T.fwRegular, color: T.mid }}>(optional)</span>
                </label>
                <textarea
                  data-testid="decision-expected-action"
                  value={expectedAction}
                  onChange={(e) => setExpectedAction(e.target.value)}
                  placeholder="e.g. Serve break notice by Q3, engage broker for sublease market…"
                  rows={2}
                  style={{
                    width:        "100%",
                    fontSize:     T.fsSm,
                    padding:      "7px 9px",
                    borderRadius: 7,
                    border:       `1px solid ${T.border}`,
                    resize:       "vertical",
                    fontFamily:   "inherit",
                    boxSizing:    "border-box",
                  }}
                />
              </div>
            </div>
          )}

          {error && (
            <div style={{ fontSize: T.fsXs, color: T.urgentRed, marginTop: 10 }}>
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          borderTop:     `1px solid ${T.border}`,
          padding:       "14px 20px",
          display:       "flex",
          justifyContent:"flex-end",
          gap:           10,
          flexShrink:    0,
        }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              padding:      "8px 18px",
              borderRadius: 9,
              background:   T.lightGray,
              color:        T.mid,
              border:       "none",
              cursor:       "pointer",
              fontSize:     T.fsSm,
              fontWeight:   T.fwSemibold,
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="decision-log-btn"
            disabled={saving}
            onClick={handleSubmit}
            style={{
              padding:      "8px 20px",
              borderRadius: 9,
              background:   saving ? T.mid : T.blue,
              color:        T.white,
              border:       "none",
              cursor:       saving ? "default" : "pointer",
              fontSize:     T.fsSm,
              fontWeight:   T.fwSemibold,
              transition:   "background .12s",
            }}
          >
            {saving ? "Logging…" : (isDeferred ? "Log deferred" : "Log decision")}
          </button>
        </div>

      </div>
    </div>
  );
}
